import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { humanError } from '../lib/errors'
import {
  visibilityBadgeClass,
  visibilityHint,
  visibilityLabel,
  type Visibility,
} from '../lib/labels'

type Wishlist = {
  id: number
  title: string
  description: string
  visibility: Visibility
  ownerName: string | null
  updatedAt: string
}

type Scope = 'mine' | 'company' | 'shared'

const SCOPE_LABELS: Record<Scope, string> = {
  mine: 'Мои',
  company: 'Компания',
  shared: 'Мне шарили',
}

export function DashboardPage() {
  const { user, logout } = useAuth()
  const [scope, setScope] = useState<Scope>('mine')
  const [wishlists, setWishlists] = useState<Wishlist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [title, setTitle] = useState('')
  const [visibility, setVisibility] = useState<Visibility>('private')
  const [creating, setCreating] = useState(false)

  async function load(nextScope = scope) {
    setError('')
    setLoading(true)
    try {
      const data = await api<{ wishlists: Wishlist[] }>(`/api/wishlists?scope=${nextScope}`)
      setWishlists(data.wishlists)
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось загрузить')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(scope)
  }, [scope])

  async function createWishlist(e: FormEvent) {
    e.preventDefault()
    setCreating(true)
    setError('')
    try {
      await api('/api/wishlists', {
        method: 'POST',
        body: JSON.stringify({ title, visibility, description: '' }),
      })
      setTitle('')
      setScope('mine')
      await load('mine')
    } catch (err) {
      setError(err instanceof ApiError ? humanError(err.message) : 'Не удалось создать')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            W<span>ish</span>
          </div>
          <div className="brand-sub">
            {user?.companyName} · код {user?.inviteCode}
          </div>
        </div>
        <div className="nav-actions">
          <span className="muted">{user?.name}</span>
          <button className="btn btn-ghost" type="button" onClick={() => void logout()}>
            Выйти
          </button>
        </div>
      </header>

      <div className="grid-2">
        <section className="panel stack" aria-labelledby="dashboard-title">
          <h1 className="section-title" id="dashboard-title">
            Вишлисты
          </h1>
          <div className="tabs" role="tablist" aria-label="Область списков">
            {(['mine', 'company', 'shared'] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={scope === key}
                className={`tab ${scope === key ? 'active' : ''}`}
                onClick={() => setScope(key)}
              >
                {SCOPE_LABELS[key]}
              </button>
            ))}
          </div>

          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading-block" role="status">
              <span className="spinner" aria-hidden="true" />
              Загружаем списки…
            </div>
          ) : wishlists.length === 0 ? (
            <div className="empty-state">
              <strong>Пока пусто</strong>
              {scope === 'mine'
                ? 'Создайте первый список справа.'
                : scope === 'company'
                  ? 'В компании ещё нет общих списков.'
                  : 'Вам пока никто не шарил списки.'}
            </div>
          ) : (
            <div className="list">
              {wishlists.map((w) => (
                <Link key={w.id} to={`/app/wishlists/${w.id}`} className="wishlist-card">
                  <div>
                    <div className="wishlist-card-title">{w.title}</div>
                    <div className="wishlist-card-meta">
                      {w.ownerName ? `${w.ownerName} · ` : ''}
                      {visibilityLabel(w.visibility)}
                    </div>
                  </div>
                  <span className={visibilityBadgeClass(w.visibility)}>
                    {visibilityLabel(w.visibility)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="panel stack">
          <h2 className="section-title">Новый список</h2>
          <p className="section-sub">Название и кто сможет видеть список.</p>
          <form className="stack" onSubmit={(e) => void createWishlist(e)}>
            <label>
              Название
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="НГ 2026"
              />
            </label>
            <label>
              Видимость
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as Visibility)}
              >
                <option value="private">{visibilityHint('private')}</option>
                <option value="shared">{visibilityHint('shared')}</option>
                <option value="company">{visibilityHint('company')}</option>
              </select>
            </label>
            <button className="btn btn-primary" type="submit" disabled={creating}>
              {creating ? 'Создаём…' : 'Создать'}
            </button>
          </form>
        </section>
      </div>
    </div>
  )
}
